import { useQueries, useQuery } from '@tanstack/react-query';
import {
  Coords,
  getPostcodeCoords,
  isValidUkPostcode,
  normaliseUkPostcode,
} from '@/utils/postcodeDistance.ts';

const ONE_DAY_MS = 1000 * 60 * 60 * 24;

/**
 * Look up the lat/lng of a single UK postcode (cached).
 * Returns null while loading or when the postcode is unknown.
 */
export function usePostcodeCoords(postcode: string | null | undefined) {
  const normalised = normaliseUkPostcode(postcode ?? '');
  const enabled = !!normalised && isValidUkPostcode(normalised);

  const query = useQuery<Coords | null>({
    queryKey: ['postcode-coords', normalised],
    queryFn: () => getPostcodeCoords(normalised),
    enabled,
    staleTime: ONE_DAY_MS,
    gcTime: ONE_DAY_MS,
  });

  return {
    coords: query.data ?? null,
    isLoading: enabled && query.isLoading,
    isResolved: enabled && !query.isLoading,
    isUnknown: enabled && !query.isLoading && !query.data,
    isInvalid: !!postcode && !enabled,
  };
}

/**
 * Look up the lat/lng of many UK postcodes in parallel (cached).
 * Returns a map keyed by the *normalised* postcode.
 */
export function usePostcodeCoordsMap(postcodes: ReadonlyArray<string | null | undefined>) {
  const normalised = Array.from(
    new Set(
      postcodes
        .map((p) => normaliseUkPostcode(p ?? ''))
        .filter((p) => !!p && isValidUkPostcode(p))
    )
  );

  const queries = useQueries({
    queries: normalised.map((postcode) => ({
      queryKey: ['postcode-coords', postcode],
      queryFn: () => getPostcodeCoords(postcode),
      staleTime: ONE_DAY_MS,
      gcTime: ONE_DAY_MS,
    })),
  });

  const map = new Map<string, Coords | null>();
  queries.forEach((q, i) => {
    map.set(normalised[i], (q.data as Coords | null | undefined) ?? null);
  });

  const isLoading = queries.some((q) => q.isLoading);

  return { coordsByPostcode: map, isLoading };
}
